<script setup lang="ts">
import { useEnv } from "@/composables/useEnv";
import { useUtility } from "@/composables/useUtility";
import { errorMessage } from "@/composables/generated/contracts";
import { useClassUnifiedService } from "../../../cbi-generator/frontend/composables/useClassUnifiedFrontendService";
import type { PersonClassEnrollment } from "../../../cbi-generator/generated/dto/PersonClassEnrollment";
import { ClassProgress } from "../../../cbi-generator/generated/dto/ClassProgress";
import { ProgramTypeKey } from "../../../cbi-generator/generated/dto/ProgramTypeKey";

const props = defineProps<{ personId: number; refreshKey?: unknown }>();
const env = useEnv();
const service = useClassUnifiedService();
const { formatDateNY } = useUtility();
const rows = shallowRef<PersonClassEnrollment[]>([]);
const loading = ref(false);
const error = ref("");
let version = 0;
onScopeDispose(() => ++version);

async function load() {
  const current = ++version;
  loading.value = true;
  error.value = "";
  try {
    const person = { personId: props.personId };
    const loaded =
      env.mode === "STAFF"
        ? await service.personEnrollmentsStaff(person)
        : await service.personEnrollmentsMember(person);
    if (current === version) rows.value = loaded;
  } catch (cause) {
    if (current === version) error.value = errorMessage(cause);
  } finally {
    if (current === version) loading.value = false;
  }
}
watch(() => [props.personId, props.refreshKey], load, { immediate: true });

function status(row: PersonClassEnrollment) {
  if (row.progress === ClassProgress.COMPLETED)
    return { text: "Completed", color: "success" };
  if (row.lastSession && row.lastSession.getTime() < Date.now())
    return { text: "Incomplete", color: "warning" };
  return { text: "In progress", color: "info" };
}
function dates(row: PersonClassEnrollment) {
  if (!row.firstSession) return "--";
  const first = formatDateNY(row.firstSession);
  const last = row.lastSession ? formatDateNY(row.lastSession) : first;
  return first === last ? first : `${first} - ${last}`;
}

const headers = [
  { key: "className", title: "Class" },
  { key: "program", title: "Program" },
  { key: "dates", title: "Dates" },
  { key: "attendance", title: "Attended" },
  { key: "status", title: "Status" },
];
</script>

<template>
  <v-alert v-if="error" type="error" role="alert" class="mb-2">
    {{ error }} <v-btn variant="text" @click="load">Retry</v-btn>
  </v-alert>
  <v-data-table
    :headers="headers"
    :items="rows"
    :loading="loading"
    item-value="signupId"
    density="compact"
    no-data-text="No current enrollments."
    hide-default-footer
    :items-per-page="-1"
    data-testid="person-class-enrollments"
  >
    <template #[`item.program`]="{ item }">
      {{
        item.programType === ProgramTypeKey.JUNIOR_PROGRAM ? "Junior" : "Adult"
      }}
    </template>
    <template #[`item.dates`]="{ item }">{{ dates(item) }}</template>
    <template #[`item.attendance`]="{ item }">
      {{ item.sessionsAttended }} / {{ item.totalSessions }}
    </template>
    <template #[`item.status`]="{ item }">
      <v-chip :color="status(item).color" size="small">{{
        status(item).text
      }}</v-chip>
    </template>
  </v-data-table>
</template>
